import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, CheckCircle, Clock, XCircle, ArrowLeft, MapPin, Building2,
  TrendingUp, AlertCircle, FileCheck
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import API from '../api';
import { useAuth } from '../auth/AuthContext';

// Module III A (district) / III B (state) — monitoring dashboard.
// Visually rich: stat cards + status pie + district/dept bars + drill-down table.
export default function StateDashboard({ scope = 'state' }) {
  const navigate = useNavigate();
  const isDistrictScope    = scope === 'district';
  const isDepartmentScope  = scope === 'department';

  const { user: userInfo, isReady: authReady, isAuthenticated } = useAuth();
  const userDistrictId   = userInfo?.district_id   ? Number(userInfo.district_id)   : null;
  const userDepartmentId = userInfo?.department_id ? Number(userInfo.department_id) : null;

  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filterDistrict, setFilterDistrict] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    if (!authReady) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    const safeGet = (url) =>
      API.get(url).then((r) => (Array.isArray(r?.data?.data) ? r.data.data : [])).catch(() => []);
    setLoading(true);
    Promise.all([
      safeGet('/applications'),
      safeGet('/tanks'),
      safeGet('/districts'),
      safeGet('/departments'),
    ]).then(([apps, tnks, dists, depts]) => {
      let scoped = apps;
      if (isDistrictScope && userDistrictId) {
        scoped = scoped.filter((a) => Number(a.district_id) === userDistrictId);
      }
      if (isDepartmentScope && userDepartmentId) {
        scoped = scoped.filter((a) => Number(a.department_id) === userDepartmentId);
      }
      setApplications(scoped);
      setTanks(tnks);
      setDistricts(dists);
      setDepartments(depts);
      setLoading(false);
    });
  }, [navigate, isDistrictScope, userDistrictId, isDepartmentScope, userDepartmentId, authReady, isAuthenticated]);

  const tankById       = useMemo(() => new Map(tanks.map((t) => [Number(t.tank_id), t])), [tanks]);
  const districtById   = useMemo(() => new Map(districts.map((d) => [Number(d.district_id), d])), [districts]);
  const departmentById = useMemo(() => new Map(departments.map((d) => [Number(d.department_id), d])), [departments]);

  const districtName = useMemo(() => {
    if (!isDistrictScope || !userDistrictId) return null;
    const row = districts.find((d) => Number(d.district_id) === userDistrictId);
    return row?.district_name || `District #${userDistrictId}`;
  }, [districts, isDistrictScope, userDistrictId]);

  const departmentName = useMemo(() => {
    if (!isDepartmentScope || !userDepartmentId) return null;
    const row = departments.find((d) => Number(d.department_id) === userDepartmentId);
    return row?.department_name || `Department #${userDepartmentId}`;
  }, [departments, isDepartmentScope, userDepartmentId]);

  const enriched = useMemo(() => applications.map((a) => {
    const tank = tankById.get(Number(a.tank_id));
    const district = districtById.get(Number(a.district_id));
    const department = departmentById.get(Number(a.department_id));
    return {
      ...a,
      id: `NOC${String(a.application_id).padStart(3, '0')}`,
      tankName: tank?.water_body_name || `Tank #${a.tank_id}`,
      districtName: district?.district_name || tank?.district_name || '-',
      blockName: tank?.block_name || '-',
      departmentName: department?.department_name || tank?.owned_by || '-',
      statusUpper: String(a.status || '').toUpperCase(),
    };
  }), [applications, tankById, districtById, departmentById]);

  const filtered = useMemo(() => enriched.filter((r) => {
    if (filterDistrict && r.districtName !== filterDistrict) return false;
    if (filterDepartment && r.departmentName !== filterDepartment) return false;
    if (filterStatus && r.statusUpper !== filterStatus) return false;
    return true;
  }), [enriched, filterDistrict, filterDepartment, filterStatus]);

  const stats = useMemo(() => {
    const acc = { total: 0, approved: 0, pending: 0, rejected: 0, returned: 0, draft: 0 };
    for (const r of enriched) {
      acc.total++;
      const s = r.statusUpper;
      if (s === 'APPROVED' || s === 'DEEMED_APPROVED') acc.approved++;
      else if (s === 'REJECTED') acc.rejected++;
      else if (s === 'RETURNED') acc.returned++;
      else if (s === 'DRAFT')    acc.draft++;
      else acc.pending++;
    }
    return acc;
  }, [enriched]);

  const statusPieData = useMemo(() => [
    { name: 'Approved', value: stats.approved, color: '#10b981' },
    { name: 'Pending',  value: stats.pending,  color: '#f97316' },
    { name: 'Returned', value: stats.returned, color: '#f59e0b' },
    { name: 'Rejected', value: stats.rejected, color: '#ef4444' },
    { name: 'Draft',    value: stats.draft,    color: '#9ca3af' },
  ].filter((s) => s.value > 0), [stats]);

  const groupedBy = (keyFn) => {
    const map = new Map();
    for (const r of enriched) {
      const k = keyFn(r) || '-';
      const cur = map.get(k) || { name: k, total: 0, approved: 0, pending: 0, rejected: 0, returned: 0 };
      cur.total++;
      const s = r.statusUpper;
      if (s === 'APPROVED' || s === 'DEEMED_APPROVED') cur.approved++;
      else if (s === 'REJECTED') cur.rejected++;
      else if (s === 'RETURNED') cur.returned++;
      else cur.pending++;
      map.set(k, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  };
  const byDistrict   = useMemo(() => groupedBy((r) => r.districtName),   [enriched]);
  const byDepartment = useMemo(() => groupedBy((r) => r.departmentName), [enriched]);

  const clearFilters = () => { setFilterDistrict(''); setFilterDepartment(''); setFilterStatus(''); };

  const statusPill = (s) => {
    const u = String(s || '').toUpperCase();
    let cls = 'bg-gray-100 text-gray-700 border-gray-300';
    if (u === 'APPROVED' || u === 'DEEMED_APPROVED') cls = 'bg-emerald-100 text-emerald-700 border-emerald-300';
    else if (u === 'REJECTED') cls = 'bg-red-100 text-red-700 border-red-300';
    else if (u === 'RETURNED') cls = 'bg-amber-100 text-amber-700 border-amber-300';
    else if (u === 'PENDING')  cls = 'bg-orange-100 text-orange-700 border-orange-300';
    return <span className={`inline-flex px-2 py-0.5 text-[11px] font-semibold rounded border ${cls}`}>{u || 'PENDING'}</span>;
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-indigo-50 min-h-screen pt-16 sm:pt-20">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-800 mr-3">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-500">Tamil Nadu NOC Portal</span>
            <span className="text-gray-300 mx-2">/</span>
            <span className="text-indigo-600 font-medium text-sm">
              {isDistrictScope
                ? `District Dashboard${districtName ? ` — ${districtName}` : ''}`
                : isDepartmentScope
                  ? `Department Dashboard${departmentName ? ` — ${departmentName}` : ''}`
                  : 'State Dashboard'}
            </span>
          </div>
          {isDepartmentScope && (
            <button
              onClick={() => navigate('/admin/department-users')}
              className="bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 px-4 py-2 rounded-lg font-medium text-sm transition-all"
              title="Manage officers for your department"
            >
              Department Users
            </button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 space-y-6">
        {/* Page header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 rounded-xl shadow-lg p-5 sm:p-6 text-white">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1">
            {isDistrictScope
              ? `District Monitoring — ${districtName || '—'}`
              : isDepartmentScope
                ? `Department Monitoring — ${departmentName || '—'}`
                : 'State-Level NOC Monitoring'}
          </h1>
          <p className="text-sm text-indigo-100">
            {isDistrictScope
              ? 'For the District Collector — real-time NOC activity within your district.'
              : isDepartmentScope
                ? 'For the Department Admin — aggregate activity across every district under your department.'
                : 'For the Secretary / Administrator — aggregate activity across every district and department.'}
          </p>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl shadow p-12 text-center text-gray-500">Loading dashboard…</div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <KpiCard color="indigo"  Icon={FileText}    label="Total Applications" value={stats.total} />
              <KpiCard color="emerald" Icon={CheckCircle} label="Approved"           value={stats.approved} />
              <KpiCard color="orange"  Icon={Clock}       label="Pending"            value={stats.pending} />
              <KpiCard color="amber"   Icon={AlertCircle} label="Returned"           value={stats.returned} />
              <KpiCard color="red"     Icon={XCircle}     label="Rejected"           value={stats.rejected} />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Status donut */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-indigo-600" /> Status distribution
                  </h3>
                </div>
                {statusPieData.length === 0 ? (
                  <div className="h-64 flex items-center justify-center text-sm text-gray-400">
                    No data
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={3}
                        label={(e) => `${e.name}: ${e.value}`}
                      >
                        {statusPieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Department bar — hidden when scope is a single department
                  (Dept Admin only sees their own dept, so a by-dept chart
                  would be a single bar). */}
              {!isDepartmentScope && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 lg:col-span-2">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-indigo-600" /> Applications by Department
                  </h3>
                  {byDepartment.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-sm text-gray-400">
                      No data
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={byDepartment.slice(0, 8)} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                        <CartesianGrid stroke="#f1f5f9" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="approved" stackId="a" name="Approved" fill="#10b981" />
                        <Bar dataKey="pending"  stackId="a" name="Pending"  fill="#f97316" />
                        <Bar dataKey="returned" stackId="a" name="Returned" fill="#f59e0b" />
                        <Bar dataKey="rejected" stackId="a" name="Rejected" fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              )}
            </div>

            {/* District chart (state scope only) */}
            {!isDistrictScope && byDistrict.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-indigo-600" /> Applications by District (top 12)
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={byDistrict.slice(0, 12)} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                    <CartesianGrid stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="approved" stackId="a" name="Approved" fill="#10b981" />
                    <Bar dataKey="pending"  stackId="a" name="Pending"  fill="#f97316" />
                    <Bar dataKey="returned" stackId="a" name="Returned" fill="#f59e0b" />
                    <Bar dataKey="rejected" stackId="a" name="Rejected" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Filtered list */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
                <span className="font-semibold text-gray-900 flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-indigo-600" />
                  Applications ({filtered.length})
                </span>
                <div className="flex items-center gap-2">
                  {!isDistrictScope && (
                    <select
                      value={filterDistrict}
                      onChange={(e) => setFilterDistrict(e.target.value)}
                      className="text-xs border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="">All Districts</option>
                      {byDistrict.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
                    </select>
                  )}
                  {!isDepartmentScope && (
                    <select
                      value={filterDepartment}
                      onChange={(e) => setFilterDepartment(e.target.value)}
                      className="text-xs border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="">All Departments</option>
                      {byDepartment.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
                    </select>
                  )}
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="">All Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="RETURNED">Returned</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="DRAFT">Draft</option>
                  </select>
                  {(filterDistrict || filterDepartment || filterStatus) && (
                    <button onClick={clearFilters} className="text-xs text-indigo-600 hover:underline">Clear</button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">NOC ID</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Tank</th>
                      {!isDistrictScope && <th className="px-3 py-2 text-left text-xs font-semibold uppercase">District</th>}
                      {!isDepartmentScope && <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Department</th>}
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Submitted</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4 + (isDistrictScope ? 0 : 1) + (isDepartmentScope ? 0 : 1)}
                          className="px-3 py-8 text-center text-gray-500"
                        >
                          No applications match the current filters.
                        </td>
                      </tr>
                    ) : filtered.slice(0, 100).map((r) => (
                      <tr key={r.application_id} className="hover:bg-indigo-50">
                        <td className="px-3 py-2 font-semibold text-indigo-600">{r.id}</td>
                        <td className="px-3 py-2">{r.tankName}</td>
                        {!isDistrictScope && <td className="px-3 py-2">{r.districtName}</td>}
                        {!isDepartmentScope && <td className="px-3 py-2">{r.departmentName}</td>}
                        <td className="px-3 py-2">{statusPill(r.status)}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length > 100 && (
                  <p className="text-xs text-center text-gray-500 p-2">
                    Showing first 100 of {filtered.length}. Use the filters above to narrow down.
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const KpiCard = ({ color, Icon, label, value }) => {
  const palette = {
    indigo:  'from-indigo-500 to-indigo-600',
    emerald: 'from-emerald-500 to-emerald-600',
    orange:  'from-orange-500 to-orange-600',
    amber:   'from-amber-500 to-amber-600',
    red:     'from-red-500 to-red-600',
  };
  return (
    <div className={`bg-gradient-to-br ${palette[color]} rounded-xl p-4 text-white shadow-md`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase opacity-90 tracking-wider mb-1">{label}</p>
          <p className="text-2xl sm:text-3xl font-bold leading-tight">{value}</p>
        </div>
        <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};
